import { type ContextMenuItem, IconButton, Tooltip } from "@design";
import { SquareMenu } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RowMenu } from "@/components/build/RowMenu";
import { type Crumb, PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { useSpaceHub } from "@/features/spaces/detail/useSpaceHub";
import { spaceCrumb } from "@/features/spaces/spaceCrumb";

/** Breadcrumb (`{parent} / title`), with the documents panel toggle and the chat's actions menu on the right. */
export function ChatHeader({
  title,
  items,
  spaceUuid,
  history,
  documentsOpen,
  onToggleDocuments,
}: Readonly<{
  title: string;
  items: ContextMenuItem[];
  /** A chat in a space sits under that space (`{Space} / title`)... */
  spaceUuid?: string | null;
  /** ...otherwise under the host's history list (Chats, or Build with AI). */
  history: { label?: string; to: string };
  documentsOpen?: boolean;
  /** Omit when the chat has no documents. */
  onToggleDocuments?: () => void;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "header" });
  const space = useSpaceHub(spaceUuid ?? "");
  const parent: Crumb = spaceUuid
    ? spaceCrumb(spaceUuid, space.data?.name ?? t("space"))
    : { label: history.label ?? t("root"), to: history.to };
  return (
    <PageBreadcrumbs
      trail={[parent]}
      current={title}
      actions={
        <>
          {onToggleDocuments && (
            <Tooltip content={t("documents")}>
              <IconButton
                icon={<SquareMenu />}
                aria-label={t("documents")}
                aria-pressed={documentsOpen}
                variant="ghost"
                size="md"
                onClick={onToggleDocuments}
              />
            </Tooltip>
          )}
          {items.length > 0 && <RowMenu name={title} items={items} />}
        </>
      }
    />
  );
}
